/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, Grid, Heading, VFlow } from 'bold-ui'
import { DecimalField, NumberField } from 'components/form'
import TipoGravidezSelectField, {
  TipoGravidezSelectFieldModel,
} from 'components/form/field/select/TipoGravidezSelectField'
import TipoPartoSelectField, { TipoPartoSelectFieldModel } from 'components/form/field/select/TipoPartoSelectField'
import { SemanasDiasField, SemanasDiasFieldModel } from 'components/form/field/SemanasDiasField'
import { isAfter, parseISO, subYears } from 'date-fns'
import { useHasPuericulturaQuery } from 'graphql/hooks.generated'
import { MetaPath } from 'util/metaPath'

import TipoAleitamentoField, { TipoAleitamentoFieldModel } from './components/TipoAleitamentoField'

export interface PrenatalPartoNascimentoModel {
  tipoParto?: TipoPartoSelectFieldModel
  tipoGravidez?: TipoGravidezSelectFieldModel
  tipoAleitamento?: TipoAleitamentoFieldModel
  apgarUm?: number
  apgarCinco?: number
  apgarDez?: number
  peso?: string
  comprimento?: string
  perimetroCefalico?: string
  idadeGestacional?: SemanasDiasFieldModel
}

export interface PrenatalPartoNascimentoProps {
  name: MetaPath<PrenatalPartoNascimentoModel>
  cidadaoDataNascimento: LocalDate
  prontuarioId: ID
}

export default function PrenatalPartoNascimento(props: PrenatalPartoNascimentoProps) {
  const { name, cidadaoDataNascimento, prontuarioId } = props
  const menosDeDoisAnos = isAfter(parseISO(cidadaoDataNascimento), subYears(new Date(), 2))

  const {
    data: { hasPuericultura },
  } = useHasPuericulturaQuery({ variables: { prontuarioId } })

  return (
    <VFlow>
      {hasPuericultura ? (
        <Alert type='info' inline>
          Pré-natal, parto e nascimento já informados.
        </Alert>
      ) : (
        <VFlow>
          <VFlow>
            <Heading level={3}>Pré-natal, parto e nascimento</Heading>

            <Grid gap={1}>
              <Cell size={4}>
                <TipoGravidezSelectField name={name.tipoGravidez} />
              </Cell>

              <Cell size={4}>
                <TipoPartoSelectField name={name.tipoParto} />
              </Cell>

              <Cell size={4} xl={3}>
                <SemanasDiasField name={name.idadeGestacional} label='Idade gestacional' />
              </Cell>
            </Grid>
          </VFlow>
          <VFlow vSpacing={0.5}>
            <Heading level={4}>Apgar</Heading>

            <Grid gap={1}>
              <Cell size={3} xl={2}>
                <NumberField name={name.apgarUm} maxLength={2} label='1º minuto' />
              </Cell>

              <Cell size={3} xl={2}>
                <NumberField name={name.apgarCinco} maxLength={2} label='5º minuto' />
              </Cell>

              <Cell size={3} xl={2}>
                <NumberField name={name.apgarDez} maxLength={2} label='10º minuto' />
              </Cell>
            </Grid>
          </VFlow>
          <VFlow vSpacing={0.5}>
            <Heading level={4}>Antropometria ao nascer</Heading>

            <Grid gap={1}>
              <Cell size={3} xl={2}>
                <DecimalField name={name.peso} label='Peso (kg)' maxLength={6} precisao={3} />
              </Cell>

              <Cell size={3} xl={2}>
                <DecimalField name={name.comprimento} label='Comprimento (cm)' maxLength={6} />
              </Cell>

              <Cell size={3} xl={2}>
                <DecimalField
                  name={name.perimetroCefalico}
                  label={
                    <span
                      css={css`
                        white-space: nowrap;
                      `}
                    >
                      Perímetro cefálico (cm)
                    </span>
                  }
                  maxLength={5}
                  precisao={1}
                />
              </Cell>
            </Grid>
          </VFlow>
        </VFlow>
      )}
      {menosDeDoisAnos && <TipoAleitamentoField name={name.tipoAleitamento} />}
    </VFlow>
  )
}
